import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
  Cell,
} from 'recharts';
import { Pill, Loader2, AlertTriangle } from 'lucide-react';

/**
 * Interface for medicine inventory data point
 */
export interface MedicineInventoryData {
  name: string;
  quantity: number;
  maxQuantity: number;
}

interface MedicineInventoryChartProps {
  data: MedicineInventoryData[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

/**
 * Get bar color based on stock level
 */
const getStockColor = (quantity: number, maxQuantity: number): string => {
  const percentage = (quantity / maxQuantity) * 100;
  if (percentage >= 60) return '#10b981'; // green - full
  if (percentage >= 30) return '#f59e0b'; // amber - warning
  return '#ef4444'; // red - low
};

/**
 * Get stock status
 */
const getStockStatus = (quantity: number, maxQuantity: number): string => {
  const percentage = (quantity / maxQuantity) * 100;
  if (percentage >= 60) return 'Adequate';
  if (percentage >= 30) return 'Low';
  return 'Critical';
};

/**
 * Custom tooltip component for medicine inventory
 */
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    const percentage = ((item.quantity / item.maxQuantity) * 100).toFixed(1);
    const status = getStockStatus(item.quantity, item.maxQuantity);

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-700 mb-2">{item.name}</p>
        <div className="space-y-1">
          <p className="text-sm text-gray-600">
            Current Stock: <span className="font-bold">{item.quantity}</span>
          </p>
          <p className="text-sm text-gray-600">
            Max Capacity: <span className="font-bold">{item.maxQuantity}</span>
          </p>
          <p className="text-sm text-gray-600">
            Stock Level: <span className="font-bold">{percentage}%</span>
          </p>
          <p className={`text-sm font-semibold ${
            status === 'Adequate' ? 'text-green-600' :
            status === 'Low' ? 'text-yellow-600' : 'text-red-600'
          }`}>
            Status: {status}
          </p>
        </div>
      </div>
    );
  }
  return null;
};

/**
 * MedicineInventoryChart - Horizontal bar chart showing medicine stock levels
 *
 * Features:
 * - Displays medicine items with current stock levels
 * - Color-coded bars (green=full, yellow=warning, red=low)
 * - Horizontal layout for better medicine name readability
 * - Interactive tooltips with stock percentage
 * - Stock level indicators and alerts
 * - Responsive design for all devices
 * - Loading and error states
 *
 * @param data - Array of medicine inventory data
 * @param loading - Loading state indicator
 * @param error - Error message if data fetch failed
 * @param height - Chart height in pixels (default: 350)
 */
const MedicineInventoryChart: React.FC<MedicineInventoryChartProps> = ({
  data,
  loading = false,
  error = null,
  height = 350,
}) => {
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        <span className="ml-2 text-gray-600">Loading inventory data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <p className="text-red-600 font-semibold">Failed to load chart</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center text-gray-500">
          <Pill className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No medicine inventory data available</p>
        </div>
      </div>
    );
  }

  // Calculate low stock items
  const lowStockItems = data.filter(item => {
    const percentage = (item.quantity / item.maxQuantity) * 100;
    return percentage < 30;
  });

  const criticalStockItems = data.filter(item => {
    const percentage = (item.quantity / item.maxQuantity) * 100;
    return percentage < 15;
  });

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <Pill className="w-5 h-5 mr-2 text-purple-600" />
          Medicine Inventory Levels
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Current stock levels across all medicines
        </p>
      </div>

      {/* Alert Section */}
      {lowStockItems.length > 0 && (
        <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
            <div>
              <p className="text-sm font-semibold text-yellow-800">
                {lowStockItems.length} medicine{lowStockItems.length > 1 ? 's' : ''} running low
              </p>
              {criticalStockItems.length > 0 && (
                <p className="text-xs text-yellow-700 mt-1">
                  {criticalStockItems.length} at critical levels - reorder immediately
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Statistics Summary */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-green-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Adequate Stock</p>
          <p className="text-lg font-bold text-green-600">
            {data.filter(item => (item.quantity / item.maxQuantity) * 100 >= 60).length}
          </p>
        </div>
        <div className="bg-yellow-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Low Stock</p>
          <p className="text-lg font-bold text-yellow-600">
            {data.filter(item => {
              const pct = (item.quantity / item.maxQuantity) * 100;
              return pct >= 30 && pct < 60;
            }).length}
          </p>
        </div>
        <div className="bg-red-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Critical</p>
          <p className="text-lg font-bold text-red-600">
            {data.filter(item => (item.quantity / item.maxQuantity) * 100 < 30).length}
          </p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            type="number"
            stroke="#666"
            tick={{ fontSize: 12 }}
          />
          <YAxis
            type="category"
            dataKey="name"
            stroke="#666"
            tick={{ fontSize: 11 }}
            width={90}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="rect"
          />
          <Bar
            dataKey="quantity"
            name="Current Stock"
            radius={[0, 8, 8, 0]}
            animationDuration={1000}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getStockColor(entry.quantity, entry.maxQuantity)}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend for stock levels */}
      <div className="flex items-center justify-center gap-4 mt-4 text-xs">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded mr-1"></div>
          <span className="text-gray-600">Adequate (≥60%)</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-yellow-500 rounded mr-1"></div>
          <span className="text-gray-600">Low (30-59%)</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 rounded mr-1"></div>
          <span className="text-gray-600">Critical (&lt;30%)</span>
        </div>
      </div>
    </div>
  );
};

export default MedicineInventoryChart;
